import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import HardBreak from '@tiptap/extension-hard-break';
import Paragraph from '@tiptap/extension-paragraph';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from 'tiptap-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBold, faItalic, faListUl, faListOl, faLink } from '@fortawesome/free-solid-svg-icons';
import { parseRichText } from '../utils/richText';
import '../styles/richText.css';

/**
 * A text box with a small formatting toolbar: bold, italic, bullet list,
 * numbered list and link. Formatting shows as you type; the value going in and
 * out is Markdown text (see utils/richText.js), so it saves into the same
 * field a plain <textarea> did and old plain text loads as it is.
 *
 *   <RichTextEditor value={form.description} onChange={(v) => setForm({ ...form, description: v })}
 *       placeholder="What needs to be done?" rows={4} />
 *
 * `required` keeps the browser's own "please fill in this field" check, and
 * `maxLength` counts the saved text, marks included.
 */
const ALLOWED_HREF = /^(https?:|mailto:|tel:)/i;

/*
 * Lines work like a plain text box: each line of the text is a paragraph in the
 * editor, saved one per line, and an empty line between them stays an empty
 * line. Markdown proper would join single lines into one paragraph and write
 * paragraphs a blank line apart, which would change old text on its first edit.
 * The saved text is read back with markdown-it `breaks`, so each new line shows.
 */
const LinePerParagraph = Paragraph.extend({
    addStorage() {
        return {
            markdown: {
                serialize(state, node, parent, index) {
                    state.renderInline(node);
                    const next = index + 1 < parent.childCount ? parent.child(index + 1) : null;
                    // a list may follow a line directly ("Steps:" then "- one"),
                    // a numbered one only when it starts at 1
                    const lineAfter = next && (next.type.name === 'paragraph'
                        || next.type.name === 'bulletList'
                        || (next.type.name === 'orderedList' && (next.attrs.start || 1) === 1));
                    if (parent.type.name === 'doc' && lineAfter) {
                        state.write();
                        state.out += '\n';
                    } else {
                        state.closeBlock(node);
                    }
                },
                parse: {}
            }
        };
    }
});

// Shift+Enter inside a paragraph or list item saves as a plain new line too.
const PlainHardBreak = HardBreak.extend({
    addStorage() {
        return {
            markdown: {
                serialize(state, node, parent, index) {
                    for (let i = index + 1; i < parent.childCount; i += 1) {
                        if (parent.child(i).type !== node.type) {
                            state.write('\n');
                            return;
                        }
                    }
                },
                parse: {}
            }
        };
    }
});

// Saved text → the editor's document, one paragraph per line (see above).
const textNodes = (runs) => runs.filter((r) => !r.br && r.text).map((r) => {
    const marks = [];
    if (r.bold) marks.push({ type: 'bold' });
    if (r.italic) marks.push({ type: 'italic' });
    if (r.href) marks.push({ type: 'link', attrs: { href: r.href } });
    return { type: 'text', text: r.text, ...(marks.length ? { marks } : {}) };
});
const paragraph = (runs) => {
    const content = textNodes(runs);
    return content.length ? { type: 'paragraph', content } : { type: 'paragraph' };
};
const splitLines = (runs) => runs.reduce((lines, r) => {
    if (r.br) lines.push([]);
    else lines[lines.length - 1].push(r);
    return lines;
}, [[]]);
const listNode = (b) => ({
    type: b.type === 'ol' ? 'orderedList' : 'bulletList',
    ...(b.type === 'ol' ? { attrs: { start: b.start } } : {}),
    content: b.items.map((blocks) => {
        const content = blocks.flatMap((x) => {
            if (x.type !== 'p') return [listNode(x)];
            // inside a list item a new line stays inside the item
            const nodes = [];
            x.runs.forEach((r) => {
                if (r.br) nodes.push({ type: 'hardBreak' });
                else nodes.push(...textNodes([r]));
            });
            return [nodes.length ? { type: 'paragraph', content: nodes } : { type: 'paragraph' }];
        });
        if (!content.length || content[0].type !== 'paragraph') content.unshift({ type: 'paragraph' });
        return { type: 'listItem', content };
    })
});
export const toEditorDoc = (text) => {
    const blocks = parseRichText(text, { linkify: false });
    const content = [];
    blocks.forEach((b, i) => {
        if (b.type === 'p') {
            if (i > 0 && blocks[i - 1].type === 'p') content.push({ type: 'paragraph' });
            splitLines(b.runs).forEach((line) => content.push(paragraph(line)));
        } else {
            content.push(listNode(b));
        }
    });
    return { type: 'doc', content: content.length ? content : [{ type: 'paragraph' }] };
};

// What the editor holds, as saved text.
export const editorText = (editor) => (editor.isEmpty
    ? ''
    : editor.storage.markdown.getMarkdown().replace(/^\n+|\n+$/g, ''));

const normalizeHref = (raw) => {
    const href = String(raw || '').trim();
    if (!href) return '';
    if (ALLOWED_HREF.test(href)) return href;
    if (/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(href)) return `mailto:${href}`;
    return `https://${href.replace(/^\/+/, '')}`;
};

// The editor's setup, shared with its test.
export const editorExtensions = (placeholder = '') => [
            StarterKit.configure({
                heading: false,
                blockquote: false,
                codeBlock: false,
                code: false,
                horizontalRule: false,
                strike: false,
                hardBreak: false,
                paragraph: false
            }),
            LinePerParagraph,
            PlainHardBreak,
            Link.configure({
                openOnClick: false,
                autolink: false,
                linkOnPaste: true,
                validate: (href) => ALLOWED_HREF.test(href),
                HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' }
            }),
            Placeholder.configure({ placeholder }),
            Markdown.configure({
                html: false,
                tightLists: true,
                linkify: false,
                breaks: true,
                transformPastedText: true,
                transformCopiedText: false
            })
];

const ToolButton = ({ label, icon, active, onClick, disabled }) => (
    <button
        type="button"
        className={`rte-btn ${active ? 'is-active' : ''}`}
        title={label}
        aria-label={label}
        aria-pressed={!!active}
        disabled={disabled}
        // keep the text selection while clicking
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
    >
        <FontAwesomeIcon icon={icon} />
    </button>
);

const RichTextEditor = ({
    value,
    initialValue = '',
    onChange,
    placeholder = '',
    rows = 4,
    maxLength,
    enforceMaxLength = true,
    required = false,
    disabled = false,
    autoFocus = false,
    id,
    name,
    className = '',
    ariaLabel
}) => {
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    // Controlled with `value`, or left to itself from `initialValue` (mountRichTextEditor).
    const controlled = value !== undefined;
    const startText = (controlled ? value : initialValue) || '';
    const lastValue = useRef(startText);
    const [text, setText] = useState(startText);
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkDraft, setLinkDraft] = useState('');
    const linkInput = useRef(null);
    const [, rerender] = useState(0);

    const editor = useEditor({
        extensions: editorExtensions(placeholder),
        content: toEditorDoc(startText),
        editable: !disabled,
        autofocus: autoFocus ? 'end' : false,
        editorProps: {
            attributes: {
                class: 'rte-content',
                ...(id ? { id } : {}),
                role: 'textbox',
                'aria-multiline': 'true',
                ...(ariaLabel || placeholder ? { 'aria-label': ariaLabel || placeholder } : {})
            }
        },
        onUpdate: ({ editor: ed }) => {
            const out = editorText(ed);
            if (enforceMaxLength && maxLength && out.length > maxLength && out.length > lastValue.current.length) {
                ed.commands.undo();
                return;
            }
            lastValue.current = out;
            setText(out);
            onChangeRef.current?.(out);
        },
        onSelectionUpdate: () => rerender((n) => n + 1),
        onTransaction: () => rerender((n) => n + 1)
    });

    // A value set from outside (a form reset, a voice draft filled in) replaces the text.
    useEffect(() => {
        if (!editor || !controlled) return;
        const next = value || '';
        if (next !== lastValue.current) {
            lastValue.current = next;
            setText(next);
            editor.commands.setContent(toEditorDoc(next), false);
        }
    }, [value, editor, controlled]);

    useEffect(() => { editor?.setEditable(!disabled); }, [editor, disabled]);
    useEffect(() => { if (linkOpen) linkInput.current?.focus(); }, [linkOpen]);

    const openLink = () => {
        if (!editor) return;
        setLinkDraft(editor.getAttributes('link').href || '');
        setLinkOpen(true);
    };
    const applyLink = () => {
        const href = normalizeHref(linkDraft);
        const chain = editor.chain().focus().extendMarkRange('link');
        if (!href) {
            chain.unsetLink().run();
        } else if (editor.state.selection.empty && !editor.isActive('link')) {
            // nothing selected: the address itself becomes the link text
            chain.insertContent({ type: 'text', text: linkDraft.trim(), marks: [{ type: 'link', attrs: { href } }] }).run();
        } else {
            chain.setLink({ href }).run();
        }
        setLinkOpen(false);
    };
    const removeLink = () => {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        setLinkOpen(false);
    };

    const minHeight = `${Math.max(2, rows) * 1.5 + 1}em`;
    const off = disabled || !editor;

    return (
        <div className={`rte ${disabled ? 'is-disabled' : ''} ${className}`.trim()} data-name={name}>
            {required && (
                // the browser's own required check, pointing back at the editor
                <textarea
                    className="rte-required"
                    tabIndex={-1}
                    aria-hidden="true"
                    required
                    value={text}
                    onChange={() => {}}
                    onFocus={() => editor?.commands.focus()}
                />
            )}
            <div className="rte-toolbar" role="toolbar" aria-label="Formatting">
                <ToolButton label="Bold (Ctrl+B)" icon={faBold} disabled={off}
                    active={editor?.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
                <ToolButton label="Italic (Ctrl+I)" icon={faItalic} disabled={off}
                    active={editor?.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
                <span className="rte-sep" aria-hidden="true" />
                <ToolButton label="Bullet list" icon={faListUl} disabled={off}
                    active={editor?.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
                <ToolButton label="Numbered list" icon={faListOl} disabled={off}
                    active={editor?.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
                <span className="rte-sep" aria-hidden="true" />
                <ToolButton label="Link" icon={faLink} disabled={off}
                    active={editor?.isActive('link') || linkOpen} onClick={openLink} />
                {maxLength ? (
                    <span className={`rte-count ${text.length > maxLength * 0.9 ? 'is-near' : ''}`}>
                        {text.length}/{maxLength}
                    </span>
                ) : null}
            </div>
            {linkOpen && (
                <div className="rte-link-row">
                    <input
                        ref={linkInput}
                        type="url"
                        className="rte-link-input"
                        placeholder="Paste or type a link"
                        value={linkDraft}
                        onChange={(e) => setLinkDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); applyLink(); }
                            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setLinkOpen(false); editor?.commands.focus(); }
                        }}
                    />
                    <button type="button" className="rte-link-btn primary" onClick={applyLink}>Apply</button>
                    {editor?.isActive('link') && (
                        <button type="button" className="rte-link-btn" onClick={removeLink}>Remove</button>
                    )}
                    <button type="button" className="rte-link-btn" onClick={() => { setLinkOpen(false); editor?.commands.focus(); }}>Cancel</button>
                </div>
            )}
            <EditorContent editor={editor} className="rte-body" style={{ minHeight }} />
        </div>
    );
};

/**
 * The editor inside a SweetAlert form, whose body is an HTML string: put an
 * empty <div id="…"> where the box goes, mount in didOpen, read in preConfirm,
 * unmount in willClose.
 *
 *   let desc;
 *   didOpen: () => { desc = mountRichTextEditor(document.getElementById('proj-desc'), { value }); },
 *   preConfirm: () => ({ description: desc.getValue() }),
 *   willClose: () => desc?.unmount(),
 */
export const mountRichTextEditor = (container, props = {}) => {
    let current = props.value || '';
    const root = createRoot(container);
    root.render(
        <RichTextEditor
            {...props}
            value={undefined}
            initialValue={current}
            onChange={(v) => { current = v; props.onChange?.(v); }}
        />
    );
    return {
        getValue: () => current,
        unmount: () => setTimeout(() => root.unmount(), 0)
    };
};

export default RichTextEditor;
